import React, { useCallback, useRef, useState } from 'react';

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 5;
const DEFAULT_STEP = 0.25;

function valueToIndex(v: number, min: number, step: number, steps: number): number {
  const idx = Math.round((v - min) / step);
  return Math.max(0, Math.min(steps - 1, idx));
}

function indexToValue(i: number, min: number, step: number): number {
  const v = min + i * step;
  return Math.round(v * 100) / 100;
}

function getStarFill(starIndex: number, activeValue: number, min: number): number {
  const low = min + starIndex;
  const high = min + starIndex + 1;
  if (activeValue >= high) return 100;
  if (activeValue <= low) return 0;
  return Math.round((activeValue - low) * 100);
}

interface StarRatingProps {
  value: number | null;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}

export default function StarRating({
  value,
  onChange,
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  step = DEFAULT_STEP,
  disabled = false,
}: StarRatingProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const steps = Math.round((max - min) / step) + 1;
  const displayValue = value ?? min;
  const activeIndex = hoverIndex !== null ? hoverIndex : valueToIndex(displayValue, min, step, steps);
  const activeValue = indexToValue(activeIndex, min, step);

  const getIndexFromEvent = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const el = containerRef.current;
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0]?.clientX : e.clientX;
      if (clientX == null) return 0;
      const x = clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      const idx = Math.floor(pct * steps);
      return Math.max(0, Math.min(steps - 1, idx));
    },
    [steps]
  );

  const handlePointer = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled) return;
      const idx = getIndexFromEvent(e);
      const v = indexToValue(idx, min, step);
      onChange(v);
    },
    [disabled, getIndexFromEvent, onChange, min, step]
  );

  const handleMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled) return;
      const idx = getIndexFromEvent(e);
      setHoverIndex(idx);
    },
    [disabled, getIndexFromEvent]
  );

  return (
    <div
      ref={containerRef}
      className="star-rating"
      role="slider"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={displayValue}
      aria-valuetext={`${displayValue} out of ${max} stars`}
      tabIndex={disabled ? undefined : 0}
      onMouseDown={handlePointer}
      onMouseMove={handleMove}
      onMouseLeave={() => setHoverIndex(null)}
      onTouchStart={handlePointer}
      onTouchMove={handlePointer}
      style={{ touchAction: 'none', userSelect: 'none' }}
    >
      <div className="star-rating__track-wrap">
        <div className="star-rating__track">
          {Array.from({ length: 5 }, (_, i) => {
            const pct = getStarFill(i, activeValue, min);
            return (
              <span key={i} className="star" aria-hidden>
                <span className="star__bg">★</span>
                <span className="star__fill-clip" style={{ width: `${pct}%` }}>
                  <span
                    className="star__fill"
                    style={pct > 0 ? { width: `${10000 / pct}%` } : undefined}
                  >
                    ★
                  </span>
                </span>
              </span>
            );
          })}
        </div>
        <div
          className="star-rating__overlay"
          onMouseDown={handlePointer}
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIndex(null)}
          onTouchStart={handlePointer}
          onTouchMove={handlePointer}
          style={{ pointerEvents: disabled ? 'none' : 'auto' }}
        />
      </div>
      <p className="star-rating__label">{displayValue.toFixed(2)} / {max}</p>
    </div>
  );
}
